/**
 * Login Prompt Modal - Shown when guest tries to access As-Is products
 */

import { X, Lock, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../contexts/LanguageContext';

interface Props {
  onClose: () => void;
}

export default function LoginPromptModal({ onClose }: Props) {
  const { t } = useLanguage();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label={t('dealsLoginPrompt.closeModal', 'Close modal')}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="w-16 h-16 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-primary-600" />
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-center text-gray-900 mb-2">
          {t('dealsLoginPrompt.title', "See What's in As-Is - Without Driving There")}
        </h2>

        {/* Description */}
        <p className="text-center text-gray-600 mb-6">
          {t('dealsLoginPrompt.description', "RUMIO is the only way to browse IKEA's in-store As-Is section online. Floor models, returns, and slightly damaged items at 50-70% off retail.")}
        </p>

        {/* Features */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary-600" />
            {t('dealsLoginPrompt.featuresTitle', 'What you get with a free account')}
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              {t('dealsLoginPrompt.feature1', 'Browse As-Is inventory at stores near you')}
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              {t('dealsLoginPrompt.feature2', 'Track up to 5 items on your checklist')}
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">✓</span>
              {t('dealsLoginPrompt.feature3', 'Get notified when checklist items go on sale')}
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">⏱</span>
              {t('dealsLoginPrompt.feature4', 'Free accounts see As-Is with a 48-hour delay')}
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Link
            to="/signup"
            className="w-full py-3 bg-primary-600 text-white text-center font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t('dealsLoginPrompt.createAccount', 'Create Free Account')}
          </Link>
          <Link
            to="/login"
            className="w-full py-3 bg-white text-gray-700 text-center font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            {t('dealsLoginPrompt.signIn', 'Sign In')}
          </Link>
        </div>

        {/* Upgrade Note */}
        <p className="mt-4 text-xs text-center text-gray-500">
          {t('dealsLoginPrompt.upgradeNote', 'Pro gets 24-hour delay. Pro+ gets real-time access.')}{' '}
          <Link to="/account/subscription" className="text-primary-600 hover:underline">
            {t('dealsLoginPrompt.comparePlans', 'Compare plans')}
          </Link>
        </p>
      </div>
    </div>
  );
}
